import * as React from 'react'
import {
	Box,
	Checkbox,
	CircularProgress,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'

import ConfirmDialog from '../core/ConfirmDialog'
import { SectionCard } from './SectionCard'
import type { UpdateWorkActivityRequest } from '../../types/Work/WorkEntry'
import {
	deleteWorkActivity,
	getWorkActivityRecords,
	updateWorkActivity,
} from '../../services/WSRService'
import { formatLocaleShortDate } from '../../services/CommonServices/DataFormatters'
import { WarActivityEditDialog } from './WarActivityEditDialog'
import { showErrorToast, showSuccessToast } from '../../services/CommonServices/alertServices'

type Props = {
	loadedTagId: number | null
	tagNumber: number | null
	activitiesLoadCount: number
	activityRecords: UpdateWorkActivityRequest[]
	setActivityRecords: (records: UpdateWorkActivityRequest[]) => void
}

export function WarActivityLog({
	loadedTagId,
	tagNumber,
	activitiesLoadCount,
	activityRecords,
	setActivityRecords,
}: Props) {
	const [loading, setLoading] = React.useState(false)
	const [loadError, setLoadError] = React.useState<string | null>(null)
	const [editRecord, setEditRecord] = React.useState<UpdateWorkActivityRequest | null>(null)
	const [editSaving, setEditSaving] = React.useState(false)
	const [deleteTarget, setDeleteTarget] = React.useState<UpdateWorkActivityRequest | null>(null)
	const [deleteSaving, setDeleteSaving] = React.useState(false)

	const fetchActivityRecords = React.useCallback(async () => {
		if (loadedTagId === null) {
			setActivityRecords([])
			return
		}
		try {
			setLoading(true)
			setLoadError(null)
			const response = await getWorkActivityRecords(loadedTagId)
			setActivityRecords(response)
		} catch (error) {
			console.error(error)
			setLoadError('Failed to load activity records.')
			setActivityRecords([])
		} finally {
			setLoading(false)
		}
	}, [loadedTagId])

	React.useEffect(() => {
		fetchActivityRecords()
	}, [fetchActivityRecords, activitiesLoadCount])

	const handleConfirmDelete = async () => {
		if (!deleteTarget) return
		try {
			setDeleteSaving(true)
			await deleteWorkActivity(deleteTarget.workActivityRegisterId)
			if (editRecord?.workActivityRegisterId === deleteTarget.workActivityRegisterId) {
				setEditRecord(null)
			}
			setDeleteTarget(null)
			showSuccessToast('Activity deleted successfully.')
			await fetchActivityRecords()
		} finally {
			setDeleteSaving(false)
		}
	}

	const handleSaveEdit = async (updated: UpdateWorkActivityRequest) => {
		try {
			setEditSaving(true)
			await updateWorkActivity({
				workActivityRegisterId: updated.workActivityRegisterId,
				plannedDate: updated.plannedDate,
				plannedHrs: updated.plannedHrs,
				percentComplete: updated.percentComplete,
				details: updated.details,
				requestDate: updated.requestDate,
				wo: updated.wo,
				billingLogEntryId: updated.billingLogEntryId,
			})
			setEditRecord(null)
			showSuccessToast('Activity updated successfully.', true)
			await fetchActivityRecords()
		} finally {
			setEditSaving(false)
		}
	}

	return (
		<SectionCard title={`WAR Activity Log - ${tagNumber !== null ? `#${tagNumber}` : ''}`}>
			<Box sx={{ mt: 1 }}>
				{loading ? (
					<Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
						<CircularProgress size={22} />
					</Box>
				) : loadError ? (
					<Box sx={{ py: 2 }}>
						<Typography variant='body2' color='error' sx={{ textAlign: 'center' }}>
							{loadError}
						</Typography>
					</Box>
				) : activityRecords.length === 0 ? (
					<Box sx={{ py: 2 }}>
						<Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center' }}>
							No activity records for this tag.
						</Typography>
					</Box>
				) : (
					<TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
						<Table size='small' stickyHeader aria-label='WAR Activity Log table'>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 600 }}>Activity Type</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>WAR ID (Summary #)</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Work Order</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Request Date</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Planned Date</TableCell>
									<TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Planned Hrs</TableCell>
									<TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>% Comp</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
									<TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Count Sheet</TableCell>
									<TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{activityRecords.map((r, idx) => (
									<TableRow
										key={r.workActivityRegisterId}
										hover
										sx={{
											backgroundColor: idx % 2 === 1 ? 'action.hover' : 'transparent',
										}}
									>
										<TableCell>{r.activityType || '—'}</TableCell>
										<TableCell>{r.warIdSummary || '—'}</TableCell>
										<TableCell>{r.wo?.trim() || '—'}</TableCell>
										<TableCell>{formatLocaleShortDate(r.createdAt, '—', 'en-GB')}</TableCell>
										<TableCell>{formatLocaleShortDate(r.requestDate, '—', 'en-GB')}</TableCell>
										<TableCell>{formatLocaleShortDate(r.plannedDate, '—', 'en-GB')}</TableCell>
										<TableCell sx={{ textAlign: 'right' }}>
											{r.plannedHrs === null || r.plannedHrs === undefined ? '—' : r.plannedHrs}
										</TableCell>
										<TableCell sx={{ textAlign: 'right' }}>
											{r.percentComplete === null || r.percentComplete === undefined
												? '—'
												: `${r.percentComplete}%`}
										</TableCell>
										<TableCell sx={{ maxWidth: 420 }}>
											<Typography
												variant='body2'
												sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
											>
												{r.details || '—'}
											</Typography>
										</TableCell>
										<TableCell sx={{ textAlign: 'center' }}>
											{/* Placeholder until Count Sheet is wired to a real field */}
											<Checkbox size='small' disabled checked={false} />
										</TableCell>
										<TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
											<Tooltip title='Edit'>
												<span>
													<IconButton
														size='small'
														onClick={() => setEditRecord(r)}
														disabled={deleteSaving}
													>
														<EditOutlinedIcon fontSize='small' color='primary' />
													</IconButton>
												</span>
											</Tooltip>
											<Tooltip title='Delete'>
												<span>
													<IconButton
														size='small'
														onClick={() => setDeleteTarget(r)}
														disabled={deleteSaving || editSaving}
													>
														<DeleteOutlineOutlinedIcon fontSize='small' color='error' />
													</IconButton>
												</span>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Box>
			<WarActivityEditDialog
				open={!!editRecord}
				record={editRecord}
				saving={editSaving}
				onClose={() => setEditRecord(null)}
				onSave={handleSaveEdit}
			/>
			<ConfirmDialog
				visible={!!deleteTarget}
				title='Delete activity record?'
				cancelButtonText='Cancel'
				confirmButtonText={deleteSaving ? 'Deleting…' : 'Delete'}
				canConfirm={!deleteSaving}
				onCancel={() => setDeleteTarget(null)}
				onConfirm={() => void handleConfirmDelete()}
			>
				{deleteTarget ? (
					<Typography variant='body2'>
						This will permanently remove the activity{' '}
						<strong>{deleteTarget.warIdSummary || deleteTarget.activityType || 'record'}</strong>.
						This action cannot be undone.
					</Typography>
				) : null}
			</ConfirmDialog>
		</SectionCard>
	)
}
